import type { IconName } from '../../../../../shared/src/lib/components/icon/icon.component';

/**
 * Action item interface for fund actions
 * Represents a single action that can be performed on a fund
 */
export interface ActionItem {
  /**
   * Unique identifier for the action type
   * Used for routing and event handling
   */
  type: string;
  /**
   * Path to the SVG icon for this action
   * Used when iconType is 'svg' (or undefined for backward compatibility)
   */
  icon?: string;
  /**
   * Icon component name (e.g., 'activity', 'external-link')
   * Used when iconType is 'component'
   */
  iconName?: IconName;
  /**
   * Color of the icon (CSS color value)
   * Used when iconType is 'component'
   */
  iconColor?: string;
  /**
   * Size of the icon in pixels
   * Used when iconType is 'component'
   * Default is 16px to match other action card icons
   */
  iconSize?: number;
  /**
   * Type of icon to display
   * - 'material': Material Design icon
   * - 'svg': SVG file path (default for backward compatibility)
   * - 'component': Icon component
   */
  iconType?: 'material' | 'svg' | 'component';
  /**
   * i18n translation key for the action title
   */
  titleKey: string;
  /**
   * i18n translation key for the action subtitle/description
   */
  subtitleKey: string;
}

/**
 * All available fund actions
 * These actions can be shown/hidden based on fund type and user permissions
 */
export const FUND_ACTIONS = {
  CONTRIBUTE: {
    type: 'contribute',
    iconName: 'contribute',
    iconColor: '#000000',
    iconSize: 16,
    iconType: 'component',
    titleKey: 'fund.actions.contribute.title',
    subtitleKey: 'fund.actions.contribute.subtitle',
  } as ActionItem,

  GRANT: {
    type: 'grant',
    iconName: 'grant',
    iconColor: '#000000',
    iconSize: 16,
    iconType: 'component',
    titleKey: 'fund.actions.grant.title',
    subtitleKey: 'fund.actions.grant.subtitle',
  } as ActionItem,

  INVEST: {
    type: 'invest',
    iconName: 'invest',
    iconColor: '#000000',
    iconSize: 16,
    iconType: 'component',
    titleKey: 'fund.actions.invest.title',
    subtitleKey: 'fund.actions.invest.subtitle',
  } as ActionItem,

  DISTRIBUTE: {
    type: 'distribute',
    iconName: 'grant', // Same icon as grant
    iconColor: '#000000',
    iconSize: 16,
    iconType: 'component',
    titleKey: 'fund.actions.distribute.title',
    subtitleKey: 'fund.actions.grant.subtitle', // Same subtitle as grant
  } as ActionItem,

  ACTIVITY: {
    type: 'activity',
    iconName: 'activity',
    iconColor: '#000000',
    iconSize: 16,
    iconType: 'component',
    titleKey: 'fund.actions.activity.title',
    subtitleKey: 'fund.actions.activity.subtitle',
  } as ActionItem,

  TRANSFER: {
    type: 'transfer',
    iconName: 'transfer',
    iconColor: '#000000',
    iconSize: 16,
    iconType: 'component',
    titleKey: 'fund.actions.transfer.title',
    subtitleKey: 'fund.actions.transfer.subtitle',
  } as ActionItem,

  CURRENCY_CONVERSION: {
    type: 'currencyConversion',
    iconName: 'currency-conversion',
    iconColor: '#000000',
    iconSize: 16,
    iconType: 'component',
    titleKey: 'fund.actions.currency_conversion.title',
    subtitleKey: 'fund.actions.currency_conversion.subtitle',
  } as ActionItem,
} as const;
